import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import FilterPanel from './components/FilterPanel';
import ProductGrid from './components/ProductGrid';
import ComparisonTable from './components/ComparisonTable';
import productService from '../../services/productService';

const ComparisonTool = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: 'all',
    priceRange: [0, 1000],
    minRating: 0,
    brands: []
  });

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [products, filters]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const allProducts = await productService?.getAllProducts();
      setProducts(allProducts);
      setFilteredProducts(allProducts);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...products];

    // Category filter
    if (filters?.category !== 'all') {
      filtered = filtered?.filter(p => p?.category === filters?.category);
    }

    // Price range filter
    filtered = filtered?.filter(
      p => p?.currentPrice >= filters?.priceRange?.[0] && p?.currentPrice <= filters?.priceRange?.[1]
    );

    // Rating filter
    filtered = filtered?.filter(p => p?.rating >= filters?.minRating);

    // Brand filter
    if (filters?.brands?.length > 0) {
      filtered = filtered?.filter(p => filters?.brands?.includes(p?.brand));
    }

    setFilteredProducts(filtered);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      category: 'all',
      priceRange: [0, 1000],
      minRating: 0,
      brands: []
    });
  };

  const handleProductSelect = (product) => {
    setSelectedProducts(prev => {
      const isSelected = prev?.some(p => p?.id === product?.id);
      if (isSelected) {
        return prev?.filter(p => p?.id !== product?.id);
      } else if (prev?.length < 4) {
        return [...prev, product];
      }
      return prev;
    });
  };

  const handleRemoveFromComparison = (productId) => {
    setSelectedProducts(prev => prev?.filter(p => p?.id !== productId));
  };

  const handleClearComparison = () => {
    setSelectedProducts([]);
  };

  const handleSaveComparison = () => {
    // Save comparison logic
    console.log('Saving comparison:', selectedProducts);
  };

  const handleExportPDF = () => {
    // Export to PDF logic
    console.log('Exporting to PDF:', selectedProducts);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <div className="bg-primary text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl font-bold mb-4">Vacuum Comparison Tool</h1>
            <p className="text-xl text-white/90 max-w-3xl">
              Compare up to 4 vacuum cleaners side by side to find the perfect match for your needs
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            <div className="lg:col-span-1">
              <FilterPanel
                filters={filters}
                onFilterChange={handleFilterChange}
                onFiltersChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                products={products}
                isOpen={true}
                onToggle={() => {}}
              />
            </div>

            <div className="lg:col-span-3">
              {selectedProducts?.length > 0 && (
                <div className="mb-8 bg-white rounded-xl shadow-subtle border border-border p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-text-primary">
                      Selected for Comparison ({selectedProducts?.length}/4)
                    </h2>
                    <button
                      onClick={handleClearComparison}
                      className="text-sm text-red-600 hover:text-red-700 font-medium"
                    >
                      Clear All
                    </button>
                  </div>
                  <ComparisonTable
                    products={selectedProducts}
                    onRemove={handleRemoveFromComparison}
                    onRemoveProduct={handleRemoveFromComparison}
                    onSaveComparison={handleSaveComparison}
                    onExportPDF={handleExportPDF}
                  />
                </div>
              )}

              <ProductGrid
                products={filteredProducts}
                selectedProducts={selectedProducts}
                onProductSelect={handleProductSelect}
                onSelectProduct={handleProductSelect}
                onRemoveProduct={handleRemoveFromComparison}
                loading={loading}
                filters={filters}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComparisonTool;